import type { IGrowthOverviewTableRow } from "../types";

import { collectSingleChildExpansionIds } from "./collectSingleChildExpansionIds";

const collectDescendantRowIds = (row: IGrowthOverviewTableRow): string[] => {
    const ids: string[] = [];

    const walk = (node: IGrowthOverviewTableRow) => {
        node.children?.forEach((child) => {
            ids.push(child.id);
            walk(child);
        });
    };

    walk(row);
    return ids;
};

const findParentRowId = (rows: IGrowthOverviewTableRow[], targetId: string): string | null => {
    const search = (nodes: IGrowthOverviewTableRow[], parentId: string | null): string | null => {
        for (const node of nodes) {
            if (node.id === targetId) {
                return parentId;
            }

            if (node.children?.length) {
                const found = search(node.children, node.id);
                if (found != null) {
                    return found;
                }
            }
        }

        return null;
    };

    return search(rows, null);
};

interface IToggleGrowthOverviewExpansionParams {
    row: IGrowthOverviewTableRow;
    rows: IGrowthOverviewTableRow[];
    expandedRowKeys: ReadonlySet<string>;
}

export const getExpandedRowKeysAfterToggle = ({
    row,
    rows,
    expandedRowKeys,
}: IToggleGrowthOverviewExpansionParams): ReadonlySet<string> => {
    const isCollapsing = expandedRowKeys.has(row.id);

    if (row.rowType === "sub-industry") {
        if (isCollapsing) {
            const next = new Set(expandedRowKeys);
            next.delete(row.id);
            return next;
        }

        const parentId = findParentRowId(rows, row.id);
        return parentId ? new Set([parentId, row.id]) : new Set([row.id]);
    }

    if (isCollapsing) {
        const next = new Set(expandedRowKeys);
        next.delete(row.id);
        collectDescendantRowIds(row).forEach((id) => next.delete(id));
        return next;
    }

    if (row.rowType === "industry") {
        return new Set(collectSingleChildExpansionIds(row));
    }

    return new Set([row.id]);
};

export const getShowAllDomainsRowKeysAfterToggle = (
    row: IGrowthOverviewTableRow,
    expandedRowKeys: ReadonlySet<string>,
    showAllDomainsRowKeys: ReadonlySet<string>,
): ReadonlySet<string> => {
    const isCollapsing = expandedRowKeys.has(row.id);

    if (isCollapsing) {
        const idsToRemove = new Set([row.id, ...collectDescendantRowIds(row)]);
        const next = new Set(showAllDomainsRowKeys);
        let changed = false;

        idsToRemove.forEach((id) => {
            if (next.delete(id)) {
                changed = true;
            }
        });

        return changed ? next : showAllDomainsRowKeys;
    }

    if (row.rowType === "sub-industry") {
        return showAllDomainsRowKeys.size > 0 ? new Set() : showAllDomainsRowKeys;
    }

    return showAllDomainsRowKeys;
};
